import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "./_layout/Sidebar";
import Topbar from "./_layout/Topbar";
import { cn } from "@/lib/utils";

const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";

/**
 * Layout — top-level chrome shared by all authenticated routes.
 *
 * Composes a fixed Sidebar (with permission-aware nav groups) and a sticky
 * Topbar (theme/lang/help/user controls). The actual page renders inside the
 * `<Outlet />`.
 *
 * The desktop sidebar can be collapsed to an icon-only rail (persisted in
 * localStorage). Mobile keeps the slide-in behaviour independent of collapse
 * so users on small screens always see full labels.
 */
export default function Layout() {
  const { user, hasPermission } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch (_) {
      // localStorage may be unavailable (private mode) — ignore silently.
    }
  }, [collapsed]);

  const toggleCollapsed = () => setCollapsed((v) => !v);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Sidebar
        user={user}
        hasPermission={hasPermission}
        mobileOpen={mobileOpen}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />

      <div className={cn("transition-[padding] duration-200", collapsed ? "lg:pl-16" : "lg:pl-60")}>
        <Topbar onToggleMobile={() => setMobileOpen((v) => !v)} />
        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </div>
  );
}
